import {useEffect, useState} from 'react';

import {
  createTier,
  deleteTierForUser,
  getTier,
  getTiers,
  updateTier,
  updateTierForUser,
} from '@/api/tiers';
import {logger} from '@/utils/logger';

import type {preSaveTier, PromotionTier} from './types';

export function usePromotions() {
  const [promotions, setPromotions] = useState<PromotionTier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getPromotions = async () => {
    setIsLoading(true);
    const tiers = await getTiers();
    setPromotions(tiers);
    setIsLoading(false);
    return tiers;
  };

  useEffect(() => {
    const fetchPromotions = async () => {
      await getPromotions();
    };
    // `getPromotions` does not catch, so report a failed initial load the way
    // the rest of this hook reports tier failures rather than letting the
    // rejection float.
    fetchPromotions().catch((error) => {
      logger.error('Failed to fetch tiers: ', error);
    });
  }, []);

  const createPromotion = async (promotionData: preSaveTier) => {
    try {
      setIsLoading(true);
      const response = await createTier(promotionData);
      setPromotions((prevPromotions) => {
        return [...prevPromotions, response];
      });
      setIsLoading(false);
      return response;
    } catch (error) {
      logger.error('Failed to create tier: ', error);
      throw error;
    }
  };

  const getPromotion = async (promotionId: string) => {
    return await getTier(promotionId);
  };

  const updatePromotion = async (
    promotionId: string,
    promotionData: Partial<PromotionTier>,
  ) => {
    setIsLoading(true);
    const response = await updateTier(promotionId, promotionData);
    setPromotions((prevPromotions) =>
      prevPromotions.map((promo) =>
        promo.id === promotionId ? {...promo, ...response} : promo,
      ),
    );
    setIsLoading(false);
  };

  const updatePromotionForUser = async (
    userId: string,
    promotionId?: string,
  ) => {
    setIsLoading(true);
    await updateTierForUser(userId, promotionId);
    setIsLoading(false);
    return;
  };

  const deletePromotionForUser = async (userId: string) => {
    setIsLoading(true);
    await deleteTierForUser(userId);
    setIsLoading(false);
    return;
  };

  return {
    createPromotion,
    getPromotion,
    getPromotions,
    updatePromotion,
    updatePromotionForUser,
    deletePromotionForUser,
    promotions,
    isLoading,
  };
}
